import type { ReactNode } from 'react';
import { toast } from 'sonner';

import { ACCEPTED_MEDIA_TYPES } from '../../constants/mediaTypes';
import { typedError } from '../../lib/typedError';
import { cn } from '../../lib/utils';
import type { MediaUploadInputProps } from '../../types/fileUpload';
import { getMediaType, validateMediaFile } from '../../utils/mediaUtils';
import { BaseUploadInput } from './BaseUploadInput';
import { MEDIA_FADE_IN_CLASS, revealMedia } from './mediaFadeIn';

const MEDIA_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,.jpg,.jpeg,.png,.gif,.webp,.mp4';

interface Props extends MediaUploadInputProps {
  existingPreview?: ReactNode;
  onRemoveExisting?: () => void;
}

export function MediaUploadInput({
  name,
  label,
  required,
  description,
  dimensions,
  value = null,
  onChange,
  onError,
  className,
  placeholderClassName,
  buttonType = 'add',
  buttonClassName,
  existingPreview,
  onRemoveExisting,
}: Props) {
  const validateFile = (file: File) => {
    const result = validateMediaFile(file);
    if (!result.valid && result.error) {
      if (result.error.type === 'FileSizeExceeded') {
        toast.error(result.error.message);
      }
      return {
        valid: false,
        error: typedError(result.error.type, result.error.message),
      };
    }
    return { valid: true };
  };

  const renderPreview = (url: string, file: File) => {
    const mediaType = getMediaType(file);
    const mediaClassName = cn(
      'block h-full w-full object-cover',
      MEDIA_FADE_IN_CLASS,
    );
    if (mediaType === 'video') {
      return (
        <video
          src={url}
          aria-label={`${label || name} preview`}
          controls
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={revealMedia}
          className={mediaClassName}
        />
      );
    }
    return (
      <img
        src={url}
        alt={`${label || name} preview`}
        onLoad={revealMedia}
        onError={revealMedia}
        className={mediaClassName}
      />
    );
  };

  return (
    <BaseUploadInput
      name={name}
      label={label}
      required={required}
      description={description}
      dimensions={dimensions}
      value={value}
      onChange={onChange}
      onError={onError}
      className={className}
      placeholderClassName={placeholderClassName}
      buttonType={buttonType}
      buttonClassName={buttonClassName}
      accept={MEDIA_ACCEPT}
      acceptedTypes={ACCEPTED_MEDIA_TYPES}
      validateFile={validateFile}
      existingPreview={existingPreview}
      onRemoveExisting={onRemoveExisting}
      renderPreview={renderPreview}
    />
  );
}
